import React, { useState, useEffect } from 'react';
import {
    Typography, Box, Button, IconButton, Tooltip, Grid,
    useTheme, useMediaQuery, TextField, Snackbar, Alert
} from '@mui/material';
import { FiTrash2, FiArrowLeft, FiBookOpen, FiPlus } from 'react-icons/fi';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Switch,
    MenuItem, FormControl, InputLabel, Select, CircularProgress
} from '@mui/material';
import api from '../../../api';
import { motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';



const ManageExams = ({ title = "Manage Exams", onAdd, onDelete }) => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [open, setOpen] = useState(false);
    const [examData, setExamData] = useState([]);

    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchQuery, setSearchQuery] = useState('');

    //add exam modal
    const [addModalOpen, setAddModalOpen] = useState(false);

    const [newExam, setNewExam] = useState({
        title: '',
        exam_type: '',
        duration_minutes: '',
        total_marks: '',
        passing_marks: '',
        status: 'Active'
    });

    //function to update status
    const handleStatusToggle = async (id, currentStatus) => {
        try {
            const updatedStatus = currentStatus === 'Active' ? false : true;

            const response = await api.put(`/exams/${id}`, { // ✅ TOKEN ADDED
                status: updatedStatus,
            });

            if (response.status === 200) {
                setExams(prev =>
                    prev.map(exam =>
                        exam.id === id
                            ? { ...exam, status: updatedStatus ? 'Active' : 'Inactive' }
                            : exam
                    )
                );

                Swal.fire({
                    icon: 'success',
                    title: 'Status Updated',
                    text: `Exam is now ${updatedStatus ? 'Active' : 'Inactive'}.`,
                    timer: 1500,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            console.error('Failed to update status:', error);
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: 'Unable to update status. Try again later.',
            });
        }
    };

    //handle submit of add exam
    const handleAddExamSubmit = async () => {
        const trimmedTitle = newExam.title.trim();

        // Client-side check for empty input
        if (!trimmedTitle) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Title',
                text: 'Please enter a valid exam title.',
                timer: 2500,
                showConfirmButton: false,
            });
            return;
        }

        // Check for duplicate title Client side check (case-sensitive)
        const duplicate = exams.find(
            (exam) =>
                typeof exam.exam === 'string' &&
                exam.exam.toLowerCase() === newExam.title.trim().toLowerCase()
        );


        if (duplicate) {
            Swal.fire({
                icon: 'warning',
                title: 'Duplicate Exam',
                text: 'An exam with this title already exists.',
                timer: 2500,
                showConfirmButton: false,
            });
            return; // prevent submission
        }
        try {
            const response = await api.post('/exams', newExam); //  TOKEN ADDED

            if (response.status === 200 || response.status === 201) {
                const newExamTransformed = {
                    id: response.data.id,
                    exam: response.data.title,
                    duration: response.data.duration_minutes,
                    totalMarks: response.data.total_marks,
                    passingMarks: response.data.passing_marks,
                    status: response.data.status ? 'Active' : 'Inactive',
                };

                setExams(prev => [...prev, newExamTransformed]);
                setAddModalOpen(false);
                setNewExam({
                    title: '',
                    duration_minutes: '',
                    total_marks: '',
                    passing_marks: '',
                    exam_type: '',
                    status: 'Active'
                });

                Swal.fire({
                    icon: 'success',
                    title: 'Exam Added!',
                    text: 'The exam has been successfully added.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Failed to add exam:', error);
            Swal.fire({
                icon: 'error',
                title: 'Failed to Add Exam',
                text: error?.response?.data?.message || 'Something went wrong. Please try again later.',
            });
        }
    };



    // Delete exam
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This action will delete the exam permanently!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await api.delete(`/exams/${id}`); // ✅ TOKEN ADDED
                    setExams(prev => prev.filter(exam => exam.id !== id));
                    Swal.fire('Deleted!', 'The exam has been deleted.', 'success');
                } catch (error) {
                    Swal.fire('Error!', 'Failed to delete the exam.', 'error');
                }
            }
        });
    };

    //edit exam
    const handleEdit = (row) => {
        setEditData({
            id: row.id,
            title: row.exam,
            duration_minutes: row.duration,
            total_marks: row.totalMarks,
            passing_marks: row.passingMarks,
            status: row.status,
        });

        setEditModalOpen(true);
    };

    //save changes handler for update exam
    const handleSaveChanges = async () => {
        // Trim and check for duplicates
        const trimmedTitle = editData.title.trim();
        const isDuplicate = exams.some(
            (exam) =>
                exam.exam.toLowerCase() === trimmedTitle.toLowerCase() &&
                exam.id !== editData.id
        );

        if (!trimmedTitle) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Title',
                text: 'Please enter a valid exam title.',
            });
            return;
        }

        if (isDuplicate) {
            Swal.fire({
                icon: 'warning',
                title: 'Duplicate Title',
                text: 'Another exam with this title already exists.',
            });
            return;
        }

        try {
            const response = await api.put(`/exams/${editData.id}`, //token added
                {
                    title: editData.title,
                    duration_minutes: editData.duration_minutes,
                    total_marks: editData.total_marks,
                    passing_marks: editData.passing_marks,
                    status: editData.status === 'Active' ? true : false,
                }
            );

            if (response.status === 200) {
                // Update local state
                setExams((prev) =>
                    prev.map((exam) =>
                        exam.id === editData.id
                            ? {
                                ...exam,
                                exam: editData.title,
                                duration: editData.duration_minutes,
                                totalMarks: editData.total_marks,
                                passingMarks: editData.passing_marks,
                                status: editData.status,
                            }
                            : exam
                    )
                );
                setEditModalOpen(false);
                // This closes the modal

                Swal.fire({
                    icon: 'success',
                    title: 'Exam Updated!',
                    text: 'The exam has been successfully updated.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error updating exam:', error);
            Swal.fire({
                icon: 'error',
                title: 'Failed to Update Exam',
                text: error?.response?.data?.message || 'Something went wrong.',
            });
        }
    };

    // Navigate to add exam page
    const handleAdd = () => {
        console.log('Add clicked');
        navigate('/admin/create-exam');
    };

    useEffect(() => {
        fetchExams();
    }, []);

    const handleBulkDelete = () => {
        const confirmDelete = window.confirm("Delete selected exams?");
        if (confirmDelete && onDelete) {
            selectedRows.forEach(id => onDelete(id));
            setSelectedRows([]);
        }
    };


    //fetch exams
    const fetchExams = async () => {
        console.log('Exam data being sent:', newExam);
        try {
            setLoading(true);
            // const response = await axios.get(`${API_URL}/api/exams`);
            const response = await api.get('/exams');
            const transformed = response.data.map((exam) => ({
                id: exam.id,
                exam: exam.title,
                duration: exam.duration_minutes,
                totalMarks: exam.total_marks,
                passingMarks: exam.passing_marks,
                status: exam.status ? 'Active' : 'Inactive',
            }));
            setExams(transformed);
        } catch (error) {
            console.error("Error fetching exams:", error);
        } finally {
            setLoading(false);
        }
    };


    const columns = [
        {
            field: 'id',
            headerName: 'Exam ID',
            flex: 0.5,
        },
        { field: 'exam', headerName: 'Exam', flex: 1 },
        { field: 'totalMarks', headerName: 'Total Marks', flex: 1 },
        { field: 'passingMarks', headerName: 'Passing Marks', flex: 1 },
        { field: 'duration', headerName: 'Duration', flex: 1 },
        { field: 'status', headerName: 'Status', flex: 0.8 },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Box display="flex" gap={1}>
                    <Switch
                        checked={params.row.status === 'Active'}
                        onChange={() => handleStatusToggle(params.row.id, params.row.status)}
                        color="primary"
                    />
                    <Tooltip title="Edit">
                        <IconButton color="primary" onClick={() => handleEdit(params.row)}>
                            ✏️
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
                            <FiTrash2 />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Toggle Status">

                    </Tooltip>
                </Box>
            )
        }
    ];

    const CustomToolbar = () => (
        <GridToolbarContainer>
            <GridToolbarExport csvOptions={{ fileName: title.replace(/\s+/g, '_').toLowerCase() }} />
            {selectedRows.length > 0 && (
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleBulkDelete}
                    sx={{ ml: 2 }}
                >
                    Delete Selected ({selectedRows.length})
                </Button>
            )}
        </GridToolbarContainer>
    );
    // ✅ Safe filter handling to avoid .toLowerCase() errors
    const filteredExams = exams.filter((exam) =>
        typeof exam.exam === 'string' &&
        exam.exam.toLowerCase().includes((searchQuery || '').toLowerCase())
    );


    return (
        <Box p={2} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box
                mb={3}
                display="flex"
                flexDirection={isSmallScreen ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                textAlign={isSmallScreen ? 'center' : 'left'}
            >
                {/* Back Button */}
                <Box
                    display="flex" alignItems="center" justifyContent={isSmallScreen ? 'center' : 'flex-start'}>

                    <IconButton onClick={() => navigate(-1)}
                        sx={{
                            borderRadius: '12px',
                            p: '8px 12px',
                            background: 'linear-gradient(135deg, #00bcd4, #007bff)',
                            color: '#fff',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #007bff, #00bcd4)',
                                transform: 'scale(1.05)'
                            }
                        }}>
                        <FiArrowLeft />
                        {!isSmall && (
                            <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>
                                Back
                            </Typography>
                        )}
                    </IconButton>
                </Box>

                {/* Title */}
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} flex={1}>
                    <Box sx={{
                        fontSize: '2rem',
                        background: '#e3f2fd',
                        borderRadius: '50%',
                        p: 1,
                        color: '#1976d2',
                        animation: 'pulseIcon 1.6s infinite',
                    }}>
                        <FiBookOpen />
                    </Box>
                    <Typography
                        variant={isSmallScreen ? 'h6' : 'h5'}
                        sx={{
                            fontWeight: 'bold',
                            background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textTransform: 'uppercase',
                            letterSpacing: '1.2px',
                            px: 1
                        }}>
                        {title}
                    </Typography>
                </Box>

                {/* Add Exam Button */}
                <Box display="flex" justifyContent={isSmallScreen ? 'center' : 'flex-end'}>
                    <Button
                        fullWidth={isSmallScreen}
                        variant="contained"
                        color="warning"
                        startIcon={<FiPlus />}
                        // onClick={handleAdd}
                        onClick={() => setAddModalOpen(true)}
                    >
                        Add Exam
                    </Button>
                </Box>

            </Box>

            {/* Table */}
            {/* Data Grid */}
            <Box height={800} sx={{ flexGrow: 1, overflow: 'auto' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGrid
                        rows={filteredExams}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        pagination
                        // checkboxSelection
                        // disableSelectionOnClick
                        onSelectionModelChange={(ids) => setSelectedRows(ids)}
                        components={{ Toolbar: CustomToolbar }}
                        loading={loading}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 10, page: 0 },
                            },
                        }}
                        sx={{
                            minHeight: 0,
                            fontFamily: 'Segoe UI',
                            border: '1px solid #ddd',
                            boxShadow: 2,
                            '& .MuiDataGrid-columnHeaders': {
                                background: 'linear-gradient(to right, #e0f3ff, #d5faff)',
                                color: '#004c99',
                                fontWeight: 'bold',
                                fontSize: '1rem'
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#fef7e0'
                            },
                            '& .MuiDataGrid-cell': {
                                fontSize: '0.95rem'
                            }
                        }}
                    />
                )}
            </Box>
            <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{ fontWeight: "bold", fontSize: "1.4rem", color: "#1976d2", display: "flex", gap: "8px" }}
                    >
                        ✏️ Edit Exam
                    </motion.div>
                    <IconButton
                        aria-label="close"
                        onClick={() => setEditModalOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8, color: "red" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        fullWidth
                        label="Exam Title"
                        margin="dense"
                        value={editData?.title || ''}
                        onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        label="Duration (minutes)"
                        type="number"
                        margin="dense"
                        value={editData?.duration_minutes || ''}
                        onChange={(e) => setEditData({ ...editData, duration_minutes: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        label="Total Marks"
                        type="number"
                        margin="dense"
                        value={editData?.total_marks || ''}
                        onChange={(e) => setEditData({ ...editData, total_marks: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        label="Passing Marks"
                        type="number"
                        margin="dense"
                        value={editData?.passing_marks || ''}
                        onChange={(e) => setEditData({ ...editData, passing_marks: e.target.value })}
                    />
                    <TextField
                        select
                        fullWidth
                        label="Status"
                        margin="dense"
                        value={editData?.status || 'Active'}
                        onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                    >
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveChanges} color="primary">
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* adding snackbar for showing toast */}
            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity="success" onClose={() => setToastOpen(false)} sx={{ width: '100%' }}>
                    {toastMessage}
                </Alert>
            </Snackbar>

            {/* this dailog is for add exam */}
            <Dialog open={addModalOpen}
                onClose={() => {
                    setAddModalOpen(false);
                    setNewExam({
                        title: '',
                        duration_minutes: '',
                        total_marks: '',
                        passing_marks: '',
                        status: 'Active'
                    });
                }}
                maxWidth="sm"
                fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between" }}>
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{ fontWeight: "bold", fontSize: "1.4rem", color: "#1976d2", display: "flex", gap: "8px" }}
                    >
                        📝 Add New Exam
                    </motion.div>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            setAddModalOpen(false);
                            setNewExam({
                                title: '',
                                duration_minutes: '',
                                total_marks: '',
                                passing_marks: '',
                                status: 'Active',
                                exam_type: ''
                            });
                        }}
                        sx={{ position: "absolute", top: 8, right: 8, color: "red" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        fullWidth
                        required
                        label="Exam Title"
                        margin="dense"
                        value={newExam.title}
                        onChange={(e) =>
                            setNewExam((prev) => ({ ...prev, title: e.target.value }))
                        }
                        error={exams.some(
                            (exam) =>
                                typeof exam.exam === 'string' &&
                                exam.exam.toLowerCase() === newExam.title.trim().toLowerCase()
                        )}
                        helperText={
                            exams.some(
                                (exam) =>
                                    typeof exam.exam === 'string' &&
                                    exam.exam.toLowerCase() === newExam.title.trim().toLowerCase()
                            )
                                ? 'This title already exists.'
                                : ''
                        }
                    />
                    <TextField
                        fullWidth
                        label="Duration (minutes)"
                        required
                        type="number"
                        margin="dense"
                        value={newExam.duration_minutes}
                        onChange={(e) => setNewExam({ ...newExam, duration_minutes: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        required
                        label="Total Marks"
                        type="number"
                        margin="dense"
                        value={newExam.total_marks}
                        onChange={(e) => setNewExam({ ...newExam, total_marks: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        required
                        label="Passing Marks"
                        type="number"
                        margin="dense"
                        value={newExam.passing_marks}
                        onChange={(e) => setNewExam({ ...newExam, passing_marks: e.target.value })}
                    />
                    <TextField
                        select
                        fullWidth
                        required
                        label="Status"
                        margin="dense"
                        value={newExam.status}
                        onChange={(e) =>
                            setNewExam({ ...newExam, status: e.target.value === 'true' })
                        }
                    >
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                    </TextField>


                    <FormControl fullWidth margin="normal" required>
                        <InputLabel>Exam Type</InputLabel>
                        <Select
                            labelId="exam-type-label"
                            value={newExam.exam_type}
                            onChange={(e) => setNewExam({ ...newExam, exam_type: e.target.value })}
                            label="Exam Type"
                        >
                            <MenuItem value="practice">practice</MenuItem>
                            <MenuItem value="mock">mock</MenuItem>
                            <MenuItem value="final">final</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={handleAddExamSubmit}>
                        Add Exam
                    </Button>
                </DialogActions>
            </Dialog>


        </Box>
    );
};

export default ManageExams;
